import { useCallback } from 'react';

import { useIntl } from 'react-intl';
import {
	createHook,
	createStore,
	type BoundActions,
	type HookFunction,
	type StoreActionApi,
} from 'react-sweet-state';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { appendADFContent } from '../../common/utils/adf';

import messages from './messages';

type State = {
	highlightActionsEditorPlaceholder: DocNode | null;
	highlightActionsEditorPlaceholderSource: 'url-param' | null;
	highlightActionsEditorPlaceholderConversationId: string | null;
};

const state = createStore({
	initialState: {
		highlightActionsEditorPlaceholder: null,
		highlightActionsEditorPlaceholderSource: null,
		highlightActionsEditorPlaceholderConversationId: null,
	} as State,
	actions: {
		setHighlightActionsEditorPlaceholder:
			(
				highlightActionsEditorPlaceholder: State['highlightActionsEditorPlaceholder'],
				highlightActionsEditorPlaceholderSource: State['highlightActionsEditorPlaceholderSource'] = null,
				highlightActionsEditorPlaceholderConversationId: State['highlightActionsEditorPlaceholderConversationId'] = null,
			): (({ setState }: StoreActionApi<State>) => void) =>
			({ setState }) => {
				setState({
					highlightActionsEditorPlaceholder,
					highlightActionsEditorPlaceholderSource,
					highlightActionsEditorPlaceholderConversationId,
				});
			},
	},
});

// exported for testing
export const useHighlightActionsEditorPlaceholderStore: HookFunction<
	State,
	BoundActions<
		State,
		{
			setHighlightActionsEditorPlaceholder: (
				highlightActionsEditorPlaceholder: State['highlightActionsEditorPlaceholder'],
				highlightActionsEditorPlaceholderSource?: State['highlightActionsEditorPlaceholderSource'],
				highlightActionsEditorPlaceholderConversationId?: State['highlightActionsEditorPlaceholderConversationId'],
			) => ({ setState }: StoreActionApi<State>) => void;
		}
	>,
	void
> = createHook(state);

const getHighlightActionPlaceholderData = (prompt: DocNode, placeholderText: string) => {
	const promptContent = appendADFContent(prompt, placeholderText);
	return promptContent;
};

export const useHighlightActionsEditorPlaceholder = (): {
	highlightActionsEditorPlaceholder: DocNode | null;
	highlightActionsEditorPlaceholderSource: State['highlightActionsEditorPlaceholderSource'];
	highlightActionsEditorPlaceholderConversationId: State['highlightActionsEditorPlaceholderConversationId'];
	insertHighlightActionsEditorPlaceholder: (
		prompt: DocNode,
		options?: {
			skipPlaceholderPrompt?: boolean;
			source?: 'url-param';
			conversationId?: string;
		},
	) => void;
	clearHighlightActionsEditorPlaceholder: () => void;
} => {
	const [
		{
			highlightActionsEditorPlaceholder,
			highlightActionsEditorPlaceholderSource,
			highlightActionsEditorPlaceholderConversationId,
		},
		{ setHighlightActionsEditorPlaceholder },
	] = useHighlightActionsEditorPlaceholderStore();
	const { formatMessage } = useIntl();

	const insertHighlightActionsEditorPlaceholder = useCallback(
		(
			prompt: DocNode,
			options?: {
				skipPlaceholderPrompt?: boolean;
				source?: 'url-param';
				conversationId?: string;
			},
		): void => {
			// When `skipPlaceholderPrompt` is set, seed the editor with the prompt
			// exactly as provided instead of appending the highlight-action
			// "Ask a question about this." trailer. This lets non-highlight callers
			// (e.g. a prompt supplied via URL param) pre-fill the input as an
			// editable draft without injecting extra text.
			const adfDocument = options?.skipPlaceholderPrompt
				? prompt
				: getHighlightActionPlaceholderData(
						prompt,
						formatMessage(messages.highlightActionsPlaceholderPrompt),
					);

			setHighlightActionsEditorPlaceholder(
				adfDocument,
				options?.source ?? null,
				options?.conversationId ?? null,
			);
		},
		[setHighlightActionsEditorPlaceholder, formatMessage],
	);

	const clearHighlightActionsEditorPlaceholder = useCallback((): void => {
		setHighlightActionsEditorPlaceholder(null);
	}, [setHighlightActionsEditorPlaceholder]);

	return {
		highlightActionsEditorPlaceholder,
		highlightActionsEditorPlaceholderSource,
		highlightActionsEditorPlaceholderConversationId,
		insertHighlightActionsEditorPlaceholder,
		clearHighlightActionsEditorPlaceholder,
	};
};
